/* eslint no-var: off, prefer-rest-params: off */
import { useEffect, useState } from 'react'
import { Business } from '../../models/Business';
import { CognitoUser } from '../../models/CognitoUser'
import { FirebaseObject } from '../../models/FirebasePostObject';
import { Holding } from '../../models/Holding';
import { useStore } from "../../store/useStore";
import { getUnixTime } from 'date-fns';
import { ILivestockUser } from '../../models/ILivestockUser';
import {
  useLocation,
} from "react-router-dom";

const apiBase = "https://api-iam.intercom.io"

interface DashboardWindow extends Window {
  Intercom?: (action: string, data?: Record<string, any>) => void;
  intercomSettings?: Record<string, any>;
}

declare let window: DashboardWindow

type IntercomProps = {
  user: CognitoUser;
  ilivestockUser: ILivestockUser;
  holdings?: FirebaseObject<Holding>[];
  businesses?: FirebaseObject<Business>[];
  opts?: {
    verticalPadding?: number;
  }
}

export const boot = async (appId: string, { user, ilivestockUser, holdings, businesses, opts }: IntercomProps): Promise<void> => {
  console.info("Booting up Intercom")

  const business = businesses ? businesses[0] : null
  const subscription = business?.data.attributes.subscription
  const primaryHolding = holdings?.filter(holding => holding.data.attributes.primaryHolding)[0]
  const {
    attributes: {
      given_name,
      family_name,
    }
  } = user

  const corePayload = {
    user_id: user.attributes.sub,
    email: user.attributes.email,
    created_at: getUnixTime(Number(user.attributes['custom:terms'])),
    name: given_name ? `${given_name} ${family_name}` : "",
    phone: business?.data.attributes.phoneMobile,
  }

  const company = business ? {
    id: business.data.attributes.code,
    name: business.data.attributes.nameLegal,
    created_at: getUnixTime(Number(user.attributes['custom:terms'])),
    plan: subscription ? `${subscription?.plan.name} - ${subscription?.status}` : ""
  } : null

  const customAttributes = {
    "Business Code": business?.data.attributes.code,
    "Address": primaryHolding?.data.attributes.address,
    "Country": primaryHolding?.data.attributes.country,
    "Completed Setup": !!ilivestockUser.hasCompletedSetup,
    "Trade Partner": ilivestockUser.customerOfOrganisation?.name,
  }

  const payload = {
    vertical_padding: opts?.verticalPadding ? opts.verticalPadding : 20,
    company: company,
    ...corePayload,
    ...customAttributes,

  }

  window.intercomSettings = {
    ...payload,
    api_base: apiBase,
    app_id: appId,
  }

  if (window.Intercom) {
    window.Intercom("shutdown")
    if (user && !businesses) {
      window.Intercom('boot', payload)
    } else {
      window.Intercom('update', payload)
    }
  }
}

const calculateVerticalPadding = (): number => {
  const wizardUrlRegex = /change-plan$|get-started$/
  return window.location.pathname.match(wizardUrlRegex) ? 80 : 20
}

export const update = (attributes: Record<string, any> = {}): void => {
  if (window.Intercom) {
    window.intercomSettings = {
      ...window.intercomSettings,
      ...attributes,
      vertical_padding: calculateVerticalPadding(),
    }
    window.Intercom('update')
  }
}

export const shutdown = (): void => {
  if (window.Intercom) {
    console.info("Shutting down Intercom")
    window.Intercom("shutdown")
    delete window.Intercom
  }
}

type Props = {
  appId: string | undefined;
}

const IntercomLiveChat: React.FC<Props> = ({ appId }) => {
  const { state } = useStore();
  const { user, ilivestockUser, holdings, businesses } = state

  useEffect(() => {
    if (!appId) return
    if (!user) return
    if (!ilivestockUser) return
    if (window.Intercom) return

    // prettier-ignore
    // @ts-expect-error: Intercom snippet, all dynamic. bleh
    if (!window.Intercom) (function() { var w = window; var ic = w.Intercom; if (typeof ic === "function") { ic('reattach_activator'); ic('update', w.intercomSettings); } else { var d = document; var i = function() { i.c(arguments); }; i.q = []; i.c = function(args) { i.q.push(args); }; w.Intercom = i; var l = function() { var s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = `https://widget.intercom.io/widget/${appId}`; var x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x); }; if (document.readyState === 'complete') { l(); } else if (w.attachEvent) { w.attachEvent('onload', l); } else { w.addEventListener('load', l, false); } } })();

    const verticalPadding = calculateVerticalPadding()

    boot(appId, {
      user,
      ilivestockUser,
      holdings,
      businesses,
      opts: {
        verticalPadding,
      }
    })

    return () => {
      if (window.Intercom) {
        console.info("Shutting down Intercom")
        window.Intercom("shutdown")
        delete window.Intercom
        delete window.intercomSettings
      }
    }
  }, [appId, user, holdings, businesses])

  return null
}

export default IntercomLiveChat
